import { z } from 'zod';
import {
  EventCents,
  EventDay,
  EventEmailList,
  EventFlag,
  EventItemKey,
  EventLookupId,
  EventNoQuery,
  EventOptionalEnum,
  EventRowId,
  EventText,
  EventWhole,
} from './eventPlanningFields';

// An in-person event's program (/api/events/:id/program, /api/events/program/*): interpretation and
// deadlines, dignitary greetings, competitions and their applicants. Prizes are whole cents.
// The lists mirror the program rules and the EventCompetitor model (the tests compare them).

export const EventInterpretationMode = z.enum(['none', 'all', 'virtual_only', 'selected']);
export type EventInterpretationMode = z.infer<typeof EventInterpretationMode>;

export const EventDignitaryReply = z.enum(['pending', 'accepted', 'video', 'declined']);
export type EventDignitaryReply = z.infer<typeof EventDignitaryReply>;

export const EventCompetitorStatus = z.enum(['applied', 'finalist', 'winner', 'not_selected', 'withdrawn']);
export type EventCompetitorStatus = z.infer<typeof EventCompetitorStatus>;

// ---------------------------------------------------------------- params

export const EventProgramParams = z.strictObject({ id: EventRowId });
export const EventProgramDignitaryParams = z.strictObject({ id: EventRowId, gid: EventItemKey });
export const EventProgramCompetitionParams = z.strictObject({ id: EventRowId, cid: EventItemKey });
export const EventProgramCompetitorParams = z.strictObject({ competitorId: EventRowId });
export const EventProgramNoQuery = EventNoQuery;

// ---------------------------------------------------------------- requests

/** PATCH /api/events/:id/program — interpretation and the speaker deadlines (days before the event). */
export const EventProgramSettingsRequest = z.strictObject({
  interpretation: z
    .strictObject({
      mode: EventOptionalEnum(
        EventInterpretationMode.options,
        `Interpretation is one of ${EventInterpretationMode.options.join(', ')}`,
      ),
      captions: EventFlag,
      interpreterEmails: EventEmailList,
      avEmails: EventEmailList,
      notes: EventText(),
    })
    .partial()
    .nullish(),
  deadlines: z
    .strictObject({
      draftDeckBusinessDays: EventWhole,
      finalDeckDays: EventWhole,
      prepMeetingDays: EventWhole,
      detailsDays: EventWhole,
    })
    .partial()
    .nullish(),
});
export type EventProgramSettingsRequest = z.input<typeof EventProgramSettingsRequest>;

/** POST /api/events/:id/program/dignitaries (name required by the service); PATCH …/:gid changes the fields sent. */
export const EventDignitaryRequest = z
  .strictObject({
    name: EventText(),
    office: EventText(),
    contact: EventText(),
    letterSentOn: EventDay,
    reply: EventOptionalEnum(EventDignitaryReply.options, `Reply is one of ${EventDignitaryReply.options.join(', ')}`),
    videoReceived: EventFlag,
    videoLink: EventText(),
    minutes: EventWhole,
    sessionId: EventLookupId,
    notes: EventText(),
  })
  .partial();
export type EventDignitaryRequest = z.input<typeof EventDignitaryRequest>;

/** A reminder date, or a reminder as the program returns it. */
const Reminder = z.union([z.string().max(40), z.looseObject({ on: EventDay }), z.null()]);

/** POST /api/events/:id/program/competitions (name required by the service); PATCH …/:cid changes the fields sent. */
export const EventCompetitionRequest = z
  .strictObject({
    name: EventText(),
    description: EventText(),
    callOpensOn: EventDay,
    deadline: EventDay,
    extendedTo: EventDay,
    reminders: z.array(Reminder).max(100).nullish(),
    timeCapMinutes: EventWhole,
    prizeCents: EventCents,
    prizeNote: EventText(),
    sessionId: EventLookupId,
    finalists: EventWhole,
  })
  .partial();
export type EventCompetitionRequest = z.input<typeof EventCompetitionRequest>;

/** POST /api/events/:id/program/competitions/:cid/reminders — mark the reminder of that date sent. */
export const EventCompetitionReminderRequest = z.strictObject({ on: EventDay });
export type EventCompetitionReminderRequest = z.input<typeof EventCompetitionReminderRequest>;

const competitorShape = {
  projectTitle: EventText(),
  status: z.enum(EventCompetitorStatus.options, {
    error: `Status is one of ${EventCompetitorStatus.options.join(', ')}`,
  }),
  presentationOrder: EventWhole,
  appliedAt: z.string().max(40).nullish(),
  notes: EventText(),
};

/** PATCH /api/events/program/competitors/:competitorId; a winner's prize goes to the ledger. */
export const EventCompetitorUpdateRequest = z.strictObject(competitorShape).partial();
export type EventCompetitorUpdateRequest = z.input<typeof EventCompetitorUpdateRequest>;

/** POST /api/events/:id/program/competitions/:cid/competitors — a member, a contact, or an email (a name for someone new). */
export const EventCompetitorRequest = z
  .strictObject({
    ...competitorShape,
    userId: EventLookupId,
    contactId: EventLookupId,
    email: z.string().max(320).nullish(),
    name: EventText(),
    organization: EventText(),
  })
  .partial();
export type EventCompetitorRequest = z.input<typeof EventCompetitorRequest>;
